import { useToast } from '@cortex/web-design-system/components/ui/Toast';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { useIssueMembers, useIssueMgmtWrite } from '../../hooks/useIssueMgmt.js';
import { describeIssueMgmtError } from '../../lib/issue-mgmt-errors.js';

/**
 * トーストを出しておく時間（7秒）。
 * @graph-connects none
 */
export const ISSUE_TOAST_DURATION_MS = 7_000;

/**
 * 保存の結果をトーストで知らせる。
 * @graph-connects none
 */
export interface IssueToastApi {
  /** 保存できたことを知らせる。operationId があれば、トーストを押すと元に戻せる。 */
  notifySaved: (
    message: string,
    options?: { operationId?: string | null; detail?: string },
  ) => void;
  /** 保存できなかった理由を知らせる。 */
  notifyError: (error: unknown) => void;
  /** メールから表示名を引く（担当の候補に無ければメール）。 */
  nameOf: (email: string) => string;
}

/**
 * 課題管理の保存の結果をトーストで知らせる。取り消せる操作のトーストは押すと「元に戻す」が走る
 * （直前の操作を打ち消す変更として保存する。その間に他の人が変えていたら戻さず、誰が変えたかを出す）。
 *
 * @graph-node {Function}
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 課題管理の保存の結果をトーストで知らせ、取り消せる操作はトーストから元に戻せるようにする。失敗は競合した人と項目まで出す
 * @graph-connects useToast [calls] トースト
 * @graph-connects useIssueMgmtWrite [calls] 元に戻す
 * @graph-connects describeIssueMgmtError [calls] 失敗の文
 */
export function useIssueToast(): IssueToastApi {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const { mutateAsync: undoAsync } = useIssueMgmtWrite('undo');
  const members = useIssueMembers();

  const nameOf = useCallback(
    (email: string) =>
      members.data?.members.find((m) => m.email === email.toLowerCase())?.displayName ?? email,
    [members.data],
  );

  const notifyError = useCallback(
    (error: unknown) => {
      showToast('error', describeIssueMgmtError(error, t, nameOf), {
        duration: ISSUE_TOAST_DURATION_MS,
      });
    },
    [nameOf, showToast, t],
  );

  const notifySaved = useCallback(
    (message: string, options?: { operationId?: string | null; detail?: string }) => {
      const operationId = options?.operationId ?? null;
      const lines = [
        ...(options?.detail === undefined ? [] : [options.detail]),
        ...(operationId === null ? [] : [t('issueMgmt.toast.undoHint')]),
      ];
      showToast('success', message, {
        duration: ISSUE_TOAST_DURATION_MS,
        ...(lines.length === 0 ? {} : { description: lines.join(' ／ ') }),
        ...(operationId === null
          ? {}
          : {
              onClick: () => {
                // 保存した小窓はもう閉じていることが多いので、呼び出しごとの onSuccess（部品が消えると呼ばれない）ではなく
                // 約束の結果で知らせる
                undoAsync({ operationId }).then(() => {
                  showToast('info', t('issueMgmt.toast.undone'), {
                    duration: ISSUE_TOAST_DURATION_MS,
                  });
                }, notifyError);
              },
            }),
      });
    },
    [notifyError, showToast, t, undoAsync],
  );

  return { notifySaved, notifyError, nameOf };
}
